import { EthereumWalletConnectors } from "@dynamic-labs/ethereum";
import { DynamicContextProvider } from "@dynamic-labs/sdk-react-core";
import { DynamicWagmiConnector } from "@dynamic-labs/wagmi-connector";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Constants, { ExecutionEnvironment } from "expo-constants";
import { Slot, useNavigationContainerRef } from "expo-router";
import React from "react";
import { Helmet } from "react-helmet";
import { I18nextProvider } from "react-i18next";
import Toast from "react-native-toast-message";
import { Provider } from "react-redux";
import { http } from "viem";
import { createConfig, WagmiProvider } from "wagmi";
import {
  mainnet,
  polygon,
  optimism,
  arbitrum,
  avalanche,
  base,
} from "wagmi/chains";

import { envConfig } from "../../environment.config";

import { GluestackUIProvider } from "@/components/ui/gluestack-ui-provider";
import store from "@/context/store";
import i18n from "@/locales/i18n";
import ErrorBoundaryProvider from "@/utils/ErrorBoundaryProvider";
import Sentry from "@/utils/SentryConstructor";
import { toastConfig } from "@/utils/ToastConfig";
import ToastReset from "@/utils/ToastReset";

import "../../global.css";

import "reflect-metadata";

const configWagmi = createConfig({
  chains: [mainnet, polygon, optimism, arbitrum, avalanche, base],
  multiInjectedProviderDiscovery: false,
  transports: {
    [mainnet.id]: http(),
    [polygon.id]: http(),
    [optimism.id]: http(),
    [arbitrum.id]: http(),
    [avalanche.id]: http(),
    [base.id]: http(),
  },
});

const queryClient = new QueryClient();

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay:
    Constants.executionEnvironment === ExecutionEnvironment.StoreClient,
});

console.log("APP_VERSION", process.env.EXPO_PUBLIC_APP_VERSION);
console.log("SENTRY_ENVIRONMENT", process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT);

Sentry.init({
  release: `izzi-wallet@${process.env.EXPO_PUBLIC_APP_VERSION}`,
  dsn: envConfig.SENTRY_DSN,
  debug: false,
  enableNative: true,
  enableNativeCrashHandling: true,
  enableCaptureFailedRequests: true,
  enableAutoSessionTracking: true,
  attachScreenshot: true,
  environment: envConfig.EXPO_PUBLIC_SENTRY_ENVIRONMENT,
  normalizeDepth: 10,
  tracesSampleRate: envConfig.SENTRY_SAMPLE_RATE,
  integrations: [navigationIntegration],
  enableNativeFramesTracking:
    Constants.executionEnvironment === ExecutionEnvironment.StoreClient,
});

function RootLayout() {
  const ref = useNavigationContainerRef();

  React.useEffect(() => {
    if (ref) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  return (
    <DynamicContextProvider
      settings={{
        environmentId: "520ff29a-f615-48cd-9672-876fb087567f",
        // @ts-ignore
        walletConnectors: [EthereumWalletConnectors],
      }}
    >
      <WagmiProvider config={configWagmi}>
        <QueryClientProvider client={queryClient}>
          <DynamicWagmiConnector>
            <Provider store={store}>
              <I18nextProvider i18n={i18n}>
                <GluestackUIProvider>
                  <Helmet>
                    <title>Izzi Wallet</title>
                  </Helmet>
                  <ErrorBoundaryProvider>
                    <Slot />
                  </ErrorBoundaryProvider>
                  <Toast config={toastConfig} />
                  <ToastReset />
                </GluestackUIProvider>
              </I18nextProvider>
            </Provider>
          </DynamicWagmiConnector>
        </QueryClientProvider>
      </WagmiProvider>
    </DynamicContextProvider>
  );
}

export default Sentry.wrap(RootLayout);
